import type { TemplateId, TemplateVarMap } from './templates';

/**
 * Send a transactional email through the Supabase send-email edge
 * function. Type-narrows `vars` against the chosen template — passing
 * the wrong shape fails at tsc time.
 *
 *   await sendEmail({
 *     supabaseUrl: env.VITE_SUPABASE_URL,
 *     anonKey: env.VITE_SUPABASE_ANON_KEY,
 *     template: 'parentInvite',
 *     to: 'parent@example.com',
 *     vars: {
 *       parent_name: 'Sam',
 *       child_name: 'Sarah',
 *       inviter_name: 'Coach Josh',
 *       team_name: 'Aurora U13',
 *       accept_url: 'https://app.4cmcoaching.com/invite/abc',
 *     },
 *   });
 *
 * Returns { ok, messageId? } — caller decides whether to surface a
 * failure to the coach (rare; backed by Postmark + retry queue) or
 * silently retry via a job queue when one exists.
 */

export interface SendEmailArgs<T extends TemplateId> {
  supabaseUrl: string;
  anonKey: string;
  template: T;
  to: string | string[];
  vars: TemplateVarMap[T];
  /** Postmark message stream override. Leave undefined for the
   *  configured default ('outbound' transactional). Set 'broadcast'
   *  for the weekly digest if you want it isolated from transactional
   *  bounce stats. */
  messageStream?: string;
  /** Optional fetch override for tests. */
  fetchImpl?: typeof fetch;
}

export interface SendEmailResult {
  ok: boolean;
  messageId?: string;
  submittedAt?: string;
  error?: string;
  status?: number;
}

export async function sendEmail<T extends TemplateId>(
  args: SendEmailArgs<T>,
): Promise<SendEmailResult> {
  const { supabaseUrl, anonKey, template, to, vars, messageStream } = args;
  const fetchImpl = args.fetchImpl ?? fetch;

  if (!supabaseUrl || !anonKey) {
    return { ok: false, error: 'supabase env not configured' };
  }

  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/send-email`;

  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${anonKey}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ template, to, vars, messageStream }),
  });

  let body: Record<string, unknown> | null = null;
  try {
    body = (await resp.json()) as Record<string, unknown>;
  } catch {
    /* ignore — body may be empty */
  }

  if (!resp.ok) {
    return {
      ok: false,
      status: resp.status,
      error:
        (body && typeof body.error === 'string' && body.error) ||
        `email send failed (${resp.status})`,
    };
  }

  return {
    ok: true,
    messageId: typeof body?.messageId === 'string' ? body.messageId : undefined,
    submittedAt: typeof body?.submittedAt === 'string' ? body.submittedAt : undefined,
  };
}
